import type { ResponsePrepTime } from "@memorang/types";
import { useRef } from "react";
import { useDevice } from "../../../../useDevice";
import AudioRecorderContainer, {
	type AudioRecorderContainerRef,
	type RecordingLimitAlertProps,
} from "../../../AudioRecorderContainer";
import Box from "../../../Box";
import { isAutoRecordMode } from "../../helpers";
import useAudioResponseCompletion from "../../hooks/useAudioResponseCompletion";
import useBeep from "../../hooks/useBeep";
import usePrepSequence from "../../hooks/usePrepSequence";
import type {
	RegisterActiveResponseCompletion,
	SessionCaptureError,
	SessionRecorderTrackingHandler,
} from "../../types";
import { ItemInstruction } from "./pte/ItemInstruction";
import { ItemStemCard } from "./pte/ItemStemCard";
import { usePrepTimerLabel } from "./pte/usePrepTimerLabel";

type Props = {
	stem: string;
	instruction: string;
	audio: string;
	handleOnRecordingComplete: (uri: string | undefined) => void;
	recordingLimitAlert?: RecordingLimitAlertProps;
	captureError: SessionCaptureError;
	handleTracking: SessionRecorderTrackingHandler;
	disabled?: boolean;
	prepTime?: ResponsePrepTime;
	timeLimit?: number;
	initialElapsedSeconds?: number;
	allowStopWhileRecording?: boolean;
	onResponseComplete?: () => void;
	registerActiveResponseCompletion?: RegisterActiveResponseCompletion;
	onLimitReachedChange?: (reached: boolean) => void;
	onRecordingActiveChange?: (active: boolean) => void;
	onPlaybackDurationChange?: (durationMillis: number) => void;
};

const ReadAloud = ({
	stem,
	instruction,
	audio,
	handleOnRecordingComplete,
	recordingLimitAlert,
	captureError,
	handleTracking,
	disabled,
	prepTime,
	timeLimit,
	initialElapsedSeconds,
	allowStopWhileRecording,
	onResponseComplete,
	registerActiveResponseCompletion,
	onLimitReachedChange,
	onRecordingActiveChange,
	onPlaybackDurationChange,
}: Props) => {
	const { isMobile } = useDevice();
	const recorderRef = useRef<AudioRecorderContainerRef | null>(null);
	const isAutoRecord = isAutoRecordMode(prepTime, timeLimit);
	const beep = useBeep();
	const formatPrepLabel = usePrepTimerLabel();

	const { handleRecordingEnded, handleRecordingComplete } =
		useAudioResponseCompletion({
			recorderRef,
			onRecordingComplete: handleOnRecordingComplete,
			isAutoRecord,
			onRecordingEnded: beep.play,
			onResponseComplete,
			registerActiveResponseCompletion,
		});

	const { phase, remainingPrepSeconds } = usePrepSequence({
		prepTime,
		timeLimit,
		hasAudioStimulus: false,
		hasRecorder: true,
		initialElapsedSeconds,
		recorderRef,
		onRecordingStarted: beep.play,
		onRecordingEnded: handleRecordingEnded,
		enabled: isAutoRecord && !(disabled || audio),
	});

	const isPrepCountdown = phase === "micDelay";

	return (
		<Box gap={isMobile ? 16 : 24}>
			<ItemInstruction instruction={instruction} />
			<ItemStemCard text={stem} />
			<AudioRecorderContainer
				ref={recorderRef}
				recordedAudio={audio}
				handleOnRecordingComplete={handleRecordingComplete}
				recordingLimitAlert={recordingLimitAlert}
				captureError={captureError}
				handleTracking={handleTracking}
				onLimitReachedChange={onLimitReachedChange}
				onRecordingActiveChange={onRecordingActiveChange}
				onPlaybackDurationChange={onPlaybackDurationChange}
				disabled={disabled}
				type={isAutoRecord ? "autoRecord" : "default"}
				showDiscardButton={!isAutoRecord}
				allowStopWhileRecording={allowStopWhileRecording}
				maxDuration={timeLimit}
				remainingSecondsOverride={
					isPrepCountdown ? remainingPrepSeconds : undefined
				}
				formatTimerLabel={isPrepCountdown ? formatPrepLabel : undefined}
			/>
		</Box>
	);
};

export default ReadAloud;
